import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';

function containsControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 31 || codePoint === 127;
  });
}

const webMfaFactorSchema = z
  .object({
    friendly_name: z
      .string()
      .trim()
      .min(1)
      .max(100)
      .refine((value) => !containsControlCharacter(value))
      .nullable()
      .optional(),
    id: z.string().uuid(),
    status: z.enum(['verified', 'unverified']),
  })
  .strip();

export type WebMfaFactor = {
  id: string;
  friendlyName: string;
};

export type WebMfaGateState = {
  currentLevel: 'aal1' | 'aal2';
  nextLevel: 'aal1' | 'aal2';
  required: boolean;
  verifiedFactors: WebMfaFactor[];
};

function assuranceLevel(value: unknown): 'aal1' | 'aal2' | null {
  return value === 'aal1' || value === 'aal2' ? value : null;
}

export async function loadWebMfaGateState(): Promise<WebMfaGateState> {
  const [factorsResult, assuranceResult] = await Promise.all([
    supabase.auth.mfa.listFactors(),
    supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
  ]);

  if (factorsResult.error || assuranceResult.error) {
    throw new Error('MFA state could not be loaded.');
  }

  const parsedFactors = z
    .array(webMfaFactorSchema)
    .max(10)
    .safeParse(factorsResult.data?.totp ?? []);
  if (!parsedFactors.success) throw new Error('MFA factors could not be validated.');

  const verifiedFactors = parsedFactors.data
    .filter((factor) => factor.status === 'verified')
    .slice(0, 10)
    .map((factor) => ({
      id: factor.id,
      friendlyName: factor.friendly_name || 'Kalonmoves Business Authenticator',
    }));
  const currentLevel = assuranceLevel(assuranceResult.data?.currentLevel);
  const nextLevel = assuranceLevel(assuranceResult.data?.nextLevel);
  if (!currentLevel || !nextLevel) {
    throw new Error('MFA assurance could not be validated.');
  }
  const required = currentLevel === 'aal1' && nextLevel === 'aal2';
  if (
    (verifiedFactors.length > 0 && currentLevel === 'aal1' && !required) ||
    (verifiedFactors.length === 0 && required)
  ) {
    throw new Error('MFA assurance did not match the enrolled factors.');
  }

  return {
    currentLevel,
    nextLevel,
    required,
    verifiedFactors,
  };
}
